import type { ReactNode } from 'react';

// AppNav — dark in-product topbar per preview/nav.html.
// Composes BrandMark + caller-supplied nav links + right-side identity slot.
//
// Bundle CSS reference:
//   .app-nav { background: var(--brand-ink); color: white; padding: 14px 24px; }
//   .app-nav nav a { color: rgba(255,255,255,0.65); }
//   .app-nav nav a.active { background: rgba(255,255,255,0.12); color: #fff; }
//
// The component is structural; callers wire route-aware NavLinks (or any
// element) into the `links` slot. Active-link styling is the caller's
// concern — pass a render function that knows about your router.

export type AppNavLinkSpec = {
  key: string;
  label: string;
  active?: boolean;
  render: (className: string) => ReactNode;
};

export type AppNavProps = {
  brandHref?: string;
  links?: ReadonlyArray<AppNavLinkSpec>;
  right?: ReactNode;
  banner?: ReactNode;
};

const linkBase =
  'inline-flex items-center px-3 py-1.5 rounded-input text-sm font-medium transition-colors';
const linkActive = 'bg-white/10 text-white';
const linkInactive = 'text-white/65 hover:text-white hover:bg-white/5';

export function AppNav({ brandHref = '/', links = [], right, banner }: AppNavProps) {
  return (
    <div className="bg-ink text-white">
      <div className="mx-auto max-w-7xl flex items-center gap-6 px-6 py-3">
        <BrandMark href={brandHref} />
        {links.length > 0 ? (
          <nav className="flex items-center gap-1">
            {links.map((l) =>
              l.render(`${linkBase} ${l.active ? linkActive : linkInactive}`),
            )}
          </nav>
        ) : null}
        {right ? <div className="ml-auto flex items-center gap-3.5">{right}</div> : null}
      </div>
      {banner}
    </div>
  );
}

// BrandMark — 24px square red mark with "r" + "remint" wordmark.
// Renders as <a href={href}> when href is provided, otherwise plain spans.

export type BrandMarkProps = {
  href?: string;
  variant?: 'on-dark' | 'on-light';
};

export function BrandMark({ href, variant = 'on-dark' }: BrandMarkProps) {
  const wordmarkColor = variant === 'on-dark' ? 'text-white' : 'text-foreground';
  const inner = (
    <span className="inline-flex items-center gap-2">
      <span
        aria-hidden
        className="inline-flex items-center justify-center w-6 h-6 rounded-input bg-primary text-white text-[15px] font-semibold leading-none font-display"
      >
        r
      </span>
      <span className={`text-base font-semibold tracking-tight font-display ${wordmarkColor}`}>
        remint
      </span>
    </span>
  );

  if (href) {
    return (
      <a href={href} aria-label="RMINT home" className="inline-flex items-center hover:opacity-90">
        {inner}
      </a>
    );
  }
  return inner;
}
